// 4 Nov 2026 (RK): English / हिंदी / Hinglish for the whole site.
// How it works: the pages stay written in English. When a visitor picks Hindi or Hinglish, every piece of
// visible text (and placeholders) whose English is in the dictionary is swapped in place - including text
// that comes from the database (Blueprint copy, lesson titles). Text nodes are only re-valued, never
// re-structured, so React keeps working (unlike browser translate widgets, which wrap text in new tags).
// Switching back to English restores the originals. Admin pages are never translated.
import React, { useState, useEffect, useRef, useSyncExternalStore } from "react";

export const LANGS = [["en", "EN", "English"], ["hi", "हिंदी", "Hindi"], ["hg", "Hinglish", "Hinglish"]];
// Dictionaries load only when a visitor picks Hindi/Hinglish, so English pages stay as light as before.
const DICTS = { hi: null, hg: null };
const LOADERS = { hi: () => import(/* webpackChunkName: "lang-hi" */ "./hi.json"), hg: () => import(/* webpackChunkName: "lang-hg" */ "./hg.json") };
function load(l) {
    if (l === "en" || DICTS[l]) return Promise.resolve();
    return LOADERS[l]().then((m) => { DICTS[l] = m.default || m; templates[l] = null; }).catch(() => {});
}
const KEY = "mv_lang";
const norm = (s) => s.replace(/\s+/g, " ").trim();

let lang = "en";
try {
    const q = new URLSearchParams(window.location.search).get("lang");
    lang = ["en", "hi", "hg"].includes(q) ? q : (localStorage.getItem(KEY) || "en");
    if (!["en", "hi", "hg"].includes(lang)) lang = "en";
} catch { lang = "en"; }

const subs = new Set();
const orig = new WeakMap();      // text node -> its English
const mine = new WeakMap();      // text node -> the value we wrote (so we can tell our writes from React's)
const origAttr = new WeakMap();  // element -> { placeholder, title, aria-label }
let templates = { hi: null, hg: null };
const missing = new Set();
if (typeof window !== "undefined") window.__i18nMissing = missing;

// keys containing {x} placeholders become patterns, e.g. "Lesson {n} of {m}"
function compile(dict) {
    const out = [];
    for (const k of Object.keys(dict)) {
        if (!k.includes("{")) continue;
        const names = [];
        const re = new RegExp("^" + k.replace(/[.*+?^$()|[\]\\]/g, "\\$&").replace(/\{(\w+)\}/g, (_, n) => { names.push(n); return "(.+?)"; }) + "$");
        out.push([re, names, dict[k]]);
    }
    return out;
}

export function translate(s, l = lang) {
    if (l === "en" || !s) return s;
    const d = DICTS[l];
    if (!d) return s;
    const k = norm(s);
    if (!k || !/[A-Za-z]/.test(k)) return s;
    let v = d[k];
    if (v === undefined) {
        if (!templates[l]) templates[l] = compile(d);
        for (const [re, names, tr] of templates[l]) {
            const m = k.match(re);
            if (m) { v = names.reduce((acc, n, i) => acc.replace(`{${n}}`, m[i + 1]), tr); break; }
        }
    }
    if (v === undefined) { missing.add(k); return s; }
    const lead = s.match(/^\s*/)[0], trail = s.match(/\s*$/)[0];
    return lead + v + trail;
}

const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE", "SVG"]);
const skipped = (el) => !el || el.closest?.("[data-notranslate],[translate=no],.notranslate") || /^\/admin/.test(window.location.pathname);

function doText(node) {
    const p = node.parentElement;
    if (!p || SKIP.has(p.tagName) || skipped(p)) return;
    const cur = node.nodeValue;
    if (mine.get(node) !== cur) orig.set(node, cur); // React (or anything) wrote new English: remember it
    const en = orig.get(node) ?? cur;
    const out = translate(en);
    if (out !== cur) { node.nodeValue = out; }
    mine.set(node, node.nodeValue);
}

const ATTRS = ["placeholder", "title", "aria-label"];
function doAttrs(el) {
    if (skipped(el)) return;
    let o = origAttr.get(el);
    for (const a of ATTRS) {
        if (!el.hasAttribute(a)) continue;
        const cur = el.getAttribute(a);
        if (!o) { o = {}; origAttr.set(el, o); }
        if (o[`_mine_${a}`] !== cur) o[a] = cur;
        const out = translate(o[a]);
        if (out !== cur) el.setAttribute(a, out);
        o[`_mine_${a}`] = el.getAttribute(a);
    }
}

function walk(root) {
    if (!root) return;
    if (root.nodeType === 3) { doText(root); return; }
    if (root.nodeType !== 1 || SKIP.has(root.tagName)) return;
    doAttrs(root);
    const tw = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    let n = tw.nextNode();
    while (n) {
        if (n.nodeType === 3) doText(n);
        else if (ATTRS.some((a) => n.hasAttribute(a))) doAttrs(n);
        n = tw.nextNode();
    }
}

let observer = null;
let queued = new Set();
let raf = 0;
function flush() {
    raf = 0;
    const list = [...queued]; queued = new Set();
    for (const n of list) if (n.isConnected) walk(n);
}
function start() {
    if (typeof document === "undefined" || observer) return;
    observer = new MutationObserver((muts) => {
        for (const m of muts) {
            if (m.type === "characterData") queued.add(m.target);
            else if (m.type === "attributes") queued.add(m.target);
            else m.addedNodes.forEach((n) => queued.add(n));
        }
        if (!raf) raf = requestAnimationFrame(flush);
    });
    observer.observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    walk(document.body);
}

export function getLang() { return lang; }
export function setLang(l) {
    if (!["en", "hi", "hg"].includes(l) || l === lang) return;
    lang = l;
    try { localStorage.setItem(KEY, l); } catch { /* ignore */ }
    document.documentElement.lang = l === "hi" ? "hi" : l === "hg" ? "hi-Latn" : "en";
    document.documentElement.dataset.lang = l;
    load(l).then(() => { if (lang === l) walk(document.body); });
    walk(document.body);
    subs.forEach((f) => f());
    try { window.dispatchEvent(new CustomEvent("mv-lang", { detail: l })); } catch { /* ignore */ }
}
export function initI18n() {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang === "hi" ? "hi" : lang === "hg" ? "hi-Latn" : "en";
    document.documentElement.dataset.lang = lang;
    start();
    if (lang !== "en") load(lang).then(() => walk(document.body));
}
export function useLang() {
    return useSyncExternalStore((f) => { subs.add(f); return () => subs.delete(f); }, () => lang, () => "en");
}
// For strings that never reach the DOM as plain text (toasts, confirm dialogs, document.title)
export const t = (s) => translate(s);

export function LangToggle({ dark = false, className = "" }) {
    const cur = useLang();
    const base = dark ? { border: "1px solid rgba(255,255,255,.18)", color: "#D9D2C6" } : { border: "1px solid #e0d6c2", color: "#57503f" };
    return (
        <div role="group" aria-label="Language" className={`inline-flex items-center rounded-full overflow-hidden text-[11px] font-semibold shrink-0 ${className}`} style={base} data-notranslate data-testid="lang-toggle">
            {LANGS.map(([k, label, full]) => (
                <button key={k} type="button" onClick={() => setLang(k)} aria-pressed={cur === k} title={full} data-testid={`lang-${k}`}
                    className="px-2.5 py-1.5 leading-none transition-colors"
                    style={cur === k ? { background: dark ? "#E5A83A" : "#17120C", color: dark ? "#080605" : "#fff" } : undefined}>
                    {label}
                </button>
            ))}
        </div>
    );
}

// Small version for tight headers (phones): shows the current language, opens the three choices.
export function LangMenu({ dark = false, className = "" }) {
    const cur = useLang();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("pointerdown", close);
        return () => document.removeEventListener("pointerdown", close);
    }, [open]);
    const label = LANGS.find((l) => l[0] === cur)?.[1] || "EN";
    const fg = dark ? "#F3EFE7" : "#17120C";
    return (
        <div ref={ref} className={`relative shrink-0 ${className}`} data-notranslate data-testid="lang-menu">
            <button type="button" onClick={() => setOpen(!open)} aria-haspopup="true" aria-expanded={open} aria-label="Change language"
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-[11px] font-semibold leading-none"
                style={{ border: dark ? "1px solid rgba(255,255,255,.22)" : "1px solid #e0d6c2", color: fg }} data-testid="lang-menu-btn">
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true"><circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" /></svg>
                {label}
            </button>
            {open && (
                <div className="absolute right-0 mt-1.5 z-[100] min-w-[130px] rounded-xl overflow-hidden shadow-xl" style={{ background: dark ? "#14100b" : "#fff", border: dark ? "1px solid rgba(255,255,255,.12)" : "1px solid #e8e1d4" }}>
                    {LANGS.map(([k, short, full]) => (
                        <button key={k} type="button" onClick={() => { setLang(k); setOpen(false); }} data-testid={`lang-menu-${k}`}
                            className="w-full text-left px-4 py-2.5 text-sm flex items-center justify-between gap-3"
                            style={{ color: fg, background: cur === k ? (dark ? "rgba(229,168,58,.15)" : "#f6f1e7") : "transparent" }}>
                            <span>{k === "hi" ? "हिंदी" : full}</span>{cur === k && <span style={{ color: "#C88A24" }}>✓</span>}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
